// Change-in-inventory posting built from a documented count.
//
// Debit/credit follows the Free Posting convention used by every manual posting
// in the app: `fraKonto` is the credit side and `tilKonto` the debit side. An
// increase in inventory debits the 14xx balance account and credits the matching
// 4x90 change account; a decrease reverses it. The change carries no VAT — it
// reclassifies between balance and cost of goods, it is not a supply.
//
// The builder is pure and produces an ordinary manual posting. Persisting it goes
// through `saveManualPostings`, so the posting is validated, audited and
// reversible on the same path as every other voucher. Saving the review itself
// never posts anything.
const buildYearClosingInventoryPosting = ({ year, evidence, categories, description }: any): any => {
  const list = (value: any): any[] => Array.isArray(value) ? value : []
  const round = (value: number): number => Math.round(value * 100) / 100
  const amount = (value: any): boolean => typeof value === 'number' && Number.isFinite(value) && value >= 0
  if (!Number.isInteger(year) || evidence?.applicability !== 'applicable') return null
  const lines = list(evidence?.lines).flatMap((line: any) => {
    const category = list(categories).find((entry: any) => entry.key === line.categoryKey)
    if (!category || !amount(line.bookValue) || !amount(line.ledgerBalance)) return []
    const change = round(Number(line.bookValue) - Number(line.ledgerBalance))
    if (Math.abs(change) < 0.005) return []
    const increase = change > 0
    const value = Math.abs(change)
    return [{
      id: `line-${category.key}`,
      categoryKey: category.key,
      change,
      fraKonto: increase ? category.changeAccountCode : category.inventoryAccountCode,
      fraBelop: value,
      fraMva: 'none',
      tilKonto: increase ? category.inventoryAccountCode : category.changeAccountCode,
      tilBelop: value,
      tilMva: 'none',
    }]
  })
  if (!lines.length) return null
  const correctionSignature = JSON.stringify(lines.map(({ categoryKey, change }: any) => [categoryKey, change]))
  let correctionHash = 2166136261
  for (let index = 0; index < correctionSignature.length; index += 1) {
    correctionHash = Math.imul(correctionHash ^ correctionSignature.charCodeAt(index), 16777619)
  }
  // The root ID identifies the inventory-adjustment chain. A revised documented
  // count uses the correction operation instead of overwriting this ID.
  return {
    id: `manual-inventory-${year}`,
    correctionOperationId: `inventory-correction-${year}-${(correctionHash >>> 0).toString(16)}`,
    date: `${year}-12-31`,
    desc: String(description || '').trim(),
    lines: lines.map(({ categoryKey, change, ...line }: any) => line),
    preview: lines.map(({ categoryKey, change, fraKonto, tilKonto, tilBelop }: any) => ({ categoryKey, change, fraKonto, tilKonto, amount: tilBelop })),
  }
}

window.PlummeYearClosingInventoryPosting = { build: buildYearClosingInventoryPosting }
